import * as THREE from "three";

export const config = {
  debug: false,
  title: "Kinderzimmer",
  model: "room2/model.glb",
  hidden_meshes_on_load: [
    "Decke",
    "Lueftung",
    "Wand_re_cut",
    "Wand_li_cut",
    "Fenster_li",
  ],
  camera: {
    position: [3.6391578965719744, 4.3630323237251485, 4.993508609312392],
    quaternion: [
      -0.25621174775630945, 0.28726101795812076, 0.08004552747933844,
      0.9194723276988144,
    ],
    lookAt: [0.023591703584522415, 0.525539312143915, -0.2281026637702244],
  },
  controls: {
    maxAzimuthAngle: Math.PI,
    minAzimuthAngle: Math.PI * 0.5,
    maxPolarAngle: Math.PI * 0.5,
    maxDistance: 12,
  },
  textures: {
    one_one: {
      file: "room2/lightmap.jpg",
      opts: {
        flipY: false,
        colorSpace: THREE.SRGBColorSpace,
        side: THREE.DoubleSide,
      },
    },
    zero_one: {
      file: "room2/lightmap-right.jpg",
      opts: {
        flipY: false,
        colorSpace: THREE.SRGBColorSpace,
        side: THREE.DoubleSide,
      },
    },
    one_zero: {
      file: "room2/lightmap-left.jpg",
      opts: {
        flipY: false,
        colorSpace: THREE.SRGBColorSpace,
        side: THREE.DoubleSide,
      },
    },
    zero_zero: {
      file: "room2/lightmap-dark.jpg",
      opts: {
        flipY: false,
        colorSpace: THREE.SRGBColorSpace,
        side: THREE.DoubleSide,
      },
    },
  },
  default_materials: {},
};

export const slots_meshes = {
  floor: ["Boden"],
  floor_boards: ["Fussleiste_re", "Fussleiste_li"],
  walls: ["Wand_re_cut", "Wand_li_cut", "Wand_li", "Wand_re"],
  window_left_blinds: ["rollo_li"],
  window_right_blinds: ["rollo_re"],
  window_left: ["Fenster_li"],
  window_right: ["Velux"],
  extras: ["Lueftung", "rollo_li", "rollo_re"],
};

export const slots_components = {
  floor: 0,
  floor_boards: 0,
  walls: 0,
};

export const component_names = {
  window_left: "Giebelfenster",
  window_right: "Dachschrägenfenster",
  window_left_blinds: "Giebelfenster-Zubehör",
  window_right_blinds: "Dachschrägenfenster-Zubehör",
  floor: "Bodenbelag",
  floor_boards: "Fußleisten",
  walls: "Wandfarbe",
  interior_walls: "Innenmauerwerk",
  sloping_roof: "Dachschräge",
  heating: "Heizung",
  ventilation: "Lüftung",
  smart_home: "Smart-Home",
  app: "Bauherren-App",
  solar: "PV-Anlage",
  vaccum: "Staubsaugeranlagen",
};

// Factory function to create components with proper context
export function createComponents(roomInstance) {
  return {
    window_left: [
      {
        title: "Fenster",
        manufacturer: "kneer",
        attributes: {
          daylight: 35,
          temperature: 6,
          sound: 5,
          comfort: 5,
        },
        explanations: {
          daylight:
            "Das Giebelfenster sorgt, je nach Ausrichtung, für viel Tageslicht.",
          temperature:
            "Durch die direkte Sonneneinstrahlung heizt sich der Raum durch das Giebelfenster tagsüber schnell auf.",
          sound:
            "Durch das Leichtglas und den asymmetrischem Glasaufbau hat das Fenster einen verbesserten Schalldämmwert von 37dB.",
          comfort:
            "Durch den zusätzlichen Einbruchschutz ist das Fenster gut gesichert.",
        },
        image: "/fenster.png",
        onSelect: () => {
          let children_to_show = ["Fenster_li", "Wand_li_cut"];
          let children_to_hide = ["Wand_li"];

          roomInstance.group.children[0].children.forEach((child) => {
            if (children_to_show.includes(child.name)) {
              child.visible = true;
            } else if (children_to_hide.includes(child.name)) {
              child.visible = false;
            }

            if (
              !roomInstance.slots_components["window_left_blinds"] &&
              roomInstance.slots_meshes["window_left_blinds"].includes(
                child.name,
              )
            ) {
              const skyTexture = roomInstance.textureLoader.load("sky.png");
              skyTexture.flipY = false;
              skyTexture.offset.set(0.47, 0);
              skyTexture.colorSpace = THREE.SRGBColorSpace;

              roomInstance.leftBlindsMaterial = new THREE.MeshStandardMaterial({
                roughness: 0.7,
                emissive: new THREE.Color(0x87ceeb),
                emissiveMap: skyTexture,
                envMapRotation: (0, 1, 1),
                emissiveIntensity: 1,
              });
              child.material = roomInstance.leftBlindsMaterial;
            }
          });
        },
        onUnselect: () => {
          let children_to_show = ["Wand_li"];
          let children_to_hide = ["Fenster_li", "Wand_li_cut"];

          roomInstance.group.children[0].children.forEach((child) => {
            if (children_to_show.includes(child.name)) {
              child.visible = true;
            } else if (children_to_hide.includes(child.name)) {
              child.visible = false;
            }
          });
        },
      },
      {
        title: "Ungeprüftes Fenster",
        manufacturer: "",
        attributes: {
          daylight: 35,
          temperature: 6,
          sound: -5,
          effluvium: 15,
        },
        explanations: {
          daylight:
            "Das Giebelfenster sorgt, je nach Ausrichtung, für viel Tageslicht.",
          temperature:
            "Durch die direkte Sonneneinstrahlung heizt sich der Raum durch das Giebelfenster tagsüber schnell auf.",
          sound:
            "Ungeprüfte Fenster können schlechtere Schalldämmeigenschaften haben und erhöhen so die Lärmbelastung von außen.",
          effluvium:
            "Ungeprüfte Fenster können mehr Schadstoffe aus Dichtungen, Klebstoffen oder Beschichtungen abgeben.",
        },
        image: "/fenster.png",
        onSelect: () => {
          let children_to_show = ["Fenster_li", "Wand_li_cut"];
          let children_to_hide = ["Wand_li"];

          roomInstance.group.children[0].children.forEach((child) => {
            if (children_to_show.includes(child.name)) {
              child.visible = true;
            } else if (children_to_hide.includes(child.name)) {
              child.visible = false;
            }

            if (
              !roomInstance.slots_components["window_left_blinds"] &&
              roomInstance.slots_meshes["window_left_blinds"].includes(
                child.name,
              )
            ) {
              const skyTexture = roomInstance.textureLoader.load("sky.png");
              skyTexture.flipY = false;
              skyTexture.offset.set(0.47, 0);
              skyTexture.colorSpace = THREE.SRGBColorSpace;

              roomInstance.leftBlindsMaterial = new THREE.MeshStandardMaterial({
                roughness: 0.7,
                emissive: new THREE.Color(0x87ceeb),
                emissiveMap: skyTexture,
                envMapRotation: (0, 1, 1),
                emissiveIntensity: 1,
              });
              child.material = roomInstance.leftBlindsMaterial;
            }
          });
        },
        onUnselect: () => {
          let children_to_show = ["Wand_li"];
          let children_to_hide = ["Fenster_li", "Wand_li_cut"];

          roomInstance.group.children[0].children.forEach((child) => {
            if (children_to_show.includes(child.name)) {
              child.visible = true;
            } else if (children_to_hide.includes(child.name)) {
              child.visible = false;
            }
          });
        },
      },
    ],
    window_right: [
      {
        title: "Dachfenster",
        manufacturer: "velux",
        attributes: {
          daylight: 36,
          temperature: 6,
        },
        explanations: {
          daylight:
            "Das Dachschrägenfenster sorgt für zusätzliches Tageslicht von oben.",
          temperature:
            "Durch die direkte Sonneneinstrahlung von oben heizt sich der Raum durch das Dachfenster schnell auf.",
        },
        image: "/dachfenster.png",
        onSelect: () => {
          let children_to_show = [
            "Velux",
            "stresni_okna_plechy003",
            "Wand_re_cut",
          ];
          let children_to_hide = ["Wand_re"];

          roomInstance.group.children[0].children.forEach((child) => {
            if (children_to_show.includes(child.name)) {
              child.visible = true;
            } else if (children_to_hide.includes(child.name)) {
              child.visible = false;
            }

            if (
              !roomInstance.slots_components["window_right_blinds"] &&
              roomInstance.slots_meshes["window_right_blinds"].includes(
                child.name,
              )
            ) {
              const skyTexture = roomInstance.textureLoader.load("sky.png");
              skyTexture.flipY = false;
              skyTexture.offset.set(0.47, 0);
              skyTexture.colorSpace = THREE.SRGBColorSpace;

              roomInstance.rightBlindsMaterial = new THREE.MeshStandardMaterial(
                {
                  roughness: 0.7,
                  emissive: new THREE.Color(0x87ceeb),
                  emissiveMap: skyTexture,
                  envMapRotation: (0, 1, 1),
                  emissiveIntensity: 1,
                },
              );
              child.material = roomInstance.rightBlindsMaterial;
            }
          });
        },
        onUnselect: () => {
          let children_to_show = ["Wand_re"];
          let children_to_hide = [
            "Velux",
            "stresni_okna_plechy003",
            "Wand_re_cut",
          ];

          roomInstance.group.children[0].children.forEach((child) => {
            if (children_to_show.includes(child.name)) {
              child.visible = true;
            } else if (children_to_hide.includes(child.name)) {
              child.visible = false;
            }
          });
          delete roomInstance.slots_components["window_right_blinds"];
        },
      },
    ],
    window_left_blinds: [
      {
        title: "Rollladen für Giebelfenster",
        manufacturer: "kneer",
        required_components: { window_left: [0, 1] },
        attributes: {
          daylight: -5,
          temperature: -4.5,
        },
        explanations: {
          daylight:
            "Ein geschlossener Rollladen sorgt dafür, dass der Raum abgedunkelt wird.",
          temperature:
            "Der Rolladen schützt vor direkter Sonneneinstrahlung und reduziert so die Raumtemperatur.",
        },
        image: "/mtex_med_18097.png",
        onSelect: () => {
          roomInstance.group.children[0].children.forEach((child) => {
            if (
              roomInstance.slots_meshes["window_left_blinds"].includes(
                child.name,
              )
            ) {
              const blindTexture =
                roomInstance.textureLoader.load("mtex_med_18097.png");
              blindTexture.flipY = false;
              blindTexture.colorSpace = THREE.SRGBColorSpace;
              blindTexture.repeat.set(2, 10);
              blindTexture.wrapS = THREE.RepeatWrapping;
              blindTexture.wrapT = THREE.RepeatWrapping;

              roomInstance.leftBlindsMaterial = new THREE.MeshStandardMaterial({
                map: blindTexture,
                roughness: 0.7,
                emissive: new THREE.Color(0x87ceeb),
                emissiveMap: blindTexture,
                emissiveIntensity: 0.4,
              });
              child.material = roomInstance.leftBlindsMaterial;
            }

            if (["Wand_li_cut"].includes(child.name)) {
              child.material = child.material.clone();
              child.material.lightMap =
                roomInstance.getLightMapMaterial("1?").map;
            }
            if (["Fenster_li"].includes(child.name)) {
              child.material = child.material.clone();
              child.material = roomInstance.getLightMapMaterial("11");
            }
          });
        },
        onUnselect: () => {},
      },
    ],
    window_left_aircon: [],
    window_right_blinds: [
      {
        title: "Dachfenster-Rollladen",
        manufacturer: "velux",
        opaque: true,
        attributes: {
          daylight: -5,
          temperature: -4.5,
        },
        explanations: {
          daylight:
            "Ein geschlossener Rollladen sorgt dafür, dass der Raum abgedunkelt wird.",
          temperature:
            "Der Rollladen schützt vor direkter Sonneneinstrahlung von oben und reduziert so die Raumtemperatur.",
        },
        image: "/mtex_med_20888.jpg",
        onSelect: () => {
          roomInstance.group.children[0].children.forEach((child) => {
            if (
              roomInstance.slots_meshes["window_right_blinds"].includes(
                child.name,
              )
            ) {
              const blindTexture =
                roomInstance.textureLoader.load("mtex_med_20888.jpg");
              blindTexture.flipY = false;
              blindTexture.colorSpace = THREE.SRGBColorSpace;
              blindTexture.repeat.set(2, 10);
              blindTexture.wrapS = THREE.RepeatWrapping;
              blindTexture.wrapT = THREE.RepeatWrapping;

              roomInstance.leftBlindsMaterial = new THREE.MeshStandardMaterial({
                map: blindTexture,
                roughness: 0.7,
                emissive: new THREE.Color(0x87ceeb),
                emissiveMap: blindTexture,
                emissiveIntensity: 1.2,
              });
              child.material = roomInstance.leftBlindsMaterial;
            }

            if (["Wand_re_cut"].includes(child.name)) {
              child.material = child.material.clone();
              child.material.lightMap =
                roomInstance.getLightMapMaterial("?1").map;
            }
            if (["Velux"].includes(child.name)) {
              child.material = child.material.clone();
              child.material = roomInstance.getLightMapMaterial("11");
            }
          });
        },
        onUnselect: () => {
          delete roomInstance.slots_components["window_right_blinds"];
        },
      },
      {
        title: "Hitzeschutzmarkise",
        manufacturer: "velux",
        opaque: false,
        attributes: {
          temperature: -3.5,
        },
        explanations: {
          temperature:
            "Die Hitzeschutzmarkise filtert die Sonnenstrahlen und reduziert so die Raumtemperatur. Dennoch lässt sie noch Tageslicht durch.",
        },
        image: "/mtex_med_20888.jpg",
        onSelect: () => {
          roomInstance.group.children[0].children.forEach((child) => {
            if (
              roomInstance.slots_meshes["window_right_blinds"].includes(
                child.name,
              )
            ) {
              const blindTexture = roomInstance.textureLoader.load("sky.png");
              blindTexture.flipY = false;
              blindTexture.colorSpace = THREE.SRGBColorSpace;
              blindTexture.offset.set(0.47, 0);

              roomInstance.leftBlindsMaterial = new THREE.MeshStandardMaterial({
                map: blindTexture,
                roughness: 0.7,
                emissive: new THREE.Color(0x87ceeb),
                emissiveMap: blindTexture,
                emissiveIntensity: 1.2,
                transparent: true,
                opacity: 0.4,
              });
              child.material = roomInstance.leftBlindsMaterial;
            }

            if (["Wand_re_cut"].includes(child.name)) {
              child.material = child.material.clone();
              child.material.lightMap =
                roomInstance.getLightMapMaterial("11").map;
            }
            if (["Velux"].includes(child.name)) {
              child.material = child.material.clone();
              child.material = roomInstance.getLightMapMaterial("11");
            }
          });
        },
        onUnselect: () => {
          delete roomInstance.slots_components["window_right_blinds"];
        },
      },
    ],
    floor: [
      {
        title: "Parkett Eiche Crema, lebhaft",
        manufacturer: "bauwerk",
        attributes: {
          sound: -10,
          effluvium: 5,
        },
        explanations: {
          sound:
            "Das Parkett kann zu Schallreflexionen führen und erhöht so die Lärmbelastung im Raum.",
          effluvium: "Das geprüfte Parkett gibt nur wenige Schadstoffe ab.",
        },
        image: "./floors/mtex_med_87902.jpg",
        onSelect: () => {
          const laminateFloorTexture = roomInstance.textureLoader.load(
            "floors/mtex_med_87902.jpg",
          );
          laminateFloorTexture.flipY = false;
          laminateFloorTexture.colorSpace = THREE.SRGBColorSpace;
          laminateFloorTexture.wrapS = THREE.RepeatWrapping;
          laminateFloorTexture.wrapT = THREE.RepeatWrapping;
          laminateFloorTexture.repeat.set(3, 3);

          roomInstance.floorMaterial = new THREE.MeshStandardMaterial({
            map: laminateFloorTexture,
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 6,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (roomInstance.slots_meshes["floor"].includes(child.name)) {
              child.material = roomInstance.floorMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Parkett Eiche Avoria, lebhaft",
        manufacturer: "bauwerk",
        attributes: {
          sound: -10,
          effluvium: 5,
        },
        explanations: {
          sound:
            "Das Parkett kann zu Schallreflexionen führen und erhöht so die Lärmbelastung im Raum.",
          effluvium: "Das geprüfte Parkett gibt nur wenige Schadstoffe ab.",
        },
        image: "./floors/mtex_med_87982.jpg",
        onSelect: () => {
          const laminateFloorTexture = roomInstance.textureLoader.load(
            "floors/mtex_med_87982.jpg",
          );
          laminateFloorTexture.flipY = false;
          laminateFloorTexture.colorSpace = THREE.SRGBColorSpace;
          laminateFloorTexture.wrapS = THREE.RepeatWrapping;
          laminateFloorTexture.wrapT = THREE.RepeatWrapping;
          laminateFloorTexture.repeat.set(3, 3);

          roomInstance.floorMaterial = new THREE.MeshStandardMaterial({
            map: laminateFloorTexture,
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 8,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (roomInstance.slots_meshes["floor"].includes(child.name)) {
              child.material = roomInstance.floorMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Parkett Eiche, sehr ruhig",
        manufacturer: "bauwerk",
        attributes: {
          sound: -10,
          effluvium: 5,
        },
        explanations: {
          sound:
            "Das Parkett kann zu Schallreflexionen führen und erhöht so die Lärmbelastung im Raum.",
          effluvium: "Das geprüfte Parkett gibt nur wenige Schadstoffe ab.",
        },
        image: "./floors/mtex_med_88045.jpg",
        onSelect: () => {
          const laminateFloorTexture = roomInstance.textureLoader.load(
            "floors/mtex_med_88045.jpg",
          );
          laminateFloorTexture.flipY = false;
          laminateFloorTexture.colorSpace = THREE.SRGBColorSpace;
          laminateFloorTexture.wrapS = THREE.RepeatWrapping;
          laminateFloorTexture.wrapT = THREE.RepeatWrapping;
          laminateFloorTexture.repeat.set(3, 3);

          roomInstance.floorMaterial = new THREE.MeshStandardMaterial({
            map: laminateFloorTexture,
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 6,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (roomInstance.slots_meshes["floor"].includes(child.name)) {
              child.material = roomInstance.floorMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Parkett Eiche rustikal",
        manufacturer: "bauwerk",
        attributes: {
          sound: -10,
          effluvium: 5,
        },
        explanations: {
          sound:
            "Das Parkett kann zu Schallreflexionen führen und erhöht so die Lärmbelastung im Raum.",
          effluvium: "Das geprüfte Parkett gibt nur wenige Schadstoffe ab.",
        },
        image: "./floors/mtex_med_87166.jpg",
        onSelect: () => {
          const laminateFloorTexture = roomInstance.textureLoader.load(
            "floors/mtex_med_87166.jpg",
          );
          laminateFloorTexture.flipY = false;
          laminateFloorTexture.colorSpace = THREE.SRGBColorSpace;
          laminateFloorTexture.wrapS = THREE.RepeatWrapping;
          laminateFloorTexture.wrapT = THREE.RepeatWrapping;
          laminateFloorTexture.repeat.set(3, 3);

          roomInstance.floorMaterial = new THREE.MeshStandardMaterial({
            map: laminateFloorTexture,
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 8,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (roomInstance.slots_meshes["floor"].includes(child.name)) {
              child.material = roomInstance.floorMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Ungeprüftes Parkett Eiche geräuchert",
        manufacturer: "",
        attributes: {
          sound: -10,
          effluvium: 25,
        },
        explanations: {
          sound:
            "Das Parkett kann zu Schallreflexionen führen und erhöht so die Lärmbelastung im Raum.",
          effluvium: "Das ungeprüfte Parkett kann Ausdünstungen verursachen.",
        },
        image: "./floors/mtex_med_87997.jpg",
        onSelect: () => {
          const laminateFloorTexture = roomInstance.textureLoader.load(
            "floors/mtex_med_87997.jpg",
          );
          laminateFloorTexture.flipY = false;
          laminateFloorTexture.colorSpace = THREE.SRGBColorSpace;
          laminateFloorTexture.wrapS = THREE.RepeatWrapping;
          laminateFloorTexture.wrapT = THREE.RepeatWrapping;
          laminateFloorTexture.repeat.set(3, 3);

          roomInstance.floorMaterial = new THREE.MeshStandardMaterial({
            map: laminateFloorTexture,
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 12,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (roomInstance.slots_meshes["floor"].includes(child.name)) {
              child.material = roomInstance.floorMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Kunststoff-Planke Eiche",
        manufacturer: "",
        attributes: {
          sound: -15,
          effluvium: 5,
        },
        explanations: {
          sound:
            "Die Kunststoff-Planke kann zu Schallreflexionen führen und erhöht so die Lärmbelastung im Raum.",
          effluvium:
            "Die geprüfte Kunststoff-Planke gibt nur wenige Schadstoffe ab.",
        },
        image: "./floors/mtex_med_87928.jpg",
        onSelect: () => {
          const laminateFloorTexture = roomInstance.textureLoader.load(
            "floors/mtex_med_87928.jpg",
          );
          laminateFloorTexture.flipY = false;
          laminateFloorTexture.colorSpace = THREE.SRGBColorSpace;
          laminateFloorTexture.wrapS = THREE.RepeatWrapping;
          laminateFloorTexture.wrapT = THREE.RepeatWrapping;
          laminateFloorTexture.repeat.set(3, 3);

          roomInstance.floorMaterial = new THREE.MeshStandardMaterial({
            map: laminateFloorTexture,
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 12,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (roomInstance.slots_meshes["floor"].includes(child.name)) {
              child.material = roomInstance.floorMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Parkett mit geprüftem Teppich",
        manufacturer: "bauwerk",
        attributes: {
          sound: 15,
          particulates: 15,
          effluvium: 5,
        },
        explanations: {
          sound:
            "Der Teppich auf dem Parkettboden erhöht die Schallabsorption und reduziert so die Lärmbelastung.",
          particulates:
            "Der Teppich auf dem Parkettboden erhöht die Partikelbelastung.",
          effluvium:
            "Das geprüfte Parkett und der geprüfte Teppich geben nur wenige Schadstoffe ab.",
        },
        image: "/floors/pvc-wood-rug.jpg",
        onSelect: () => {
          const textures = {
            baseColorTexture: roomInstance.textureLoader.load(
              "floors/pvc-wood-rug.jpg",
            ),
            ambientOcclusionTexture: roomInstance.textureLoader.load(
              "floors/pvc-wood-rug.jpg",
            ),
            normalTexture: roomInstance.textureLoader.load(
              "floors/pvc-wood-rug.jpg",
            ),
          };

          Object.keys(textures).forEach((key) => {
            textures[key].flipY = false;
            textures[key].encoding = THREE.sRGBColorSpace;
            textures[key].wrapS = THREE.RepeatWrapping;
            textures[key].wrapT = THREE.RepeatWrapping;
            textures[key].repeat.set(5, 5);
          });

          roomInstance.floorMaterial = new THREE.MeshStandardMaterial({
            map: textures.baseColorTexture,
            aoMap: textures.ambientOcclusionTexture,
            normalMap: textures.normalTexture,
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 5,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (roomInstance.slots_meshes["floor"].includes(child.name)) {
              child.material = roomInstance.floorMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Geprüfter Teppichboden als Auslegeware",
        manufacturer: "",
        attributes: {
          sound: 30,
          particulates: 25,
          effluvium: 5,
        },
        explanations: {
          sound:
            "Der Teppichboden als Auslegeware erhöht die Schallabsorption deutlich und reduziert so die Lärmbelastung erheblich.",
          particulates:
            "Der Teppichboden als Auslegeware bindet viele Staubpartikel und erhöht so die Partikelbelastung.",
          effluvium:
            "Der geprüfte Teppichboden als Auslegeware gibt nur wenige Schadstoffe ab.",
        },
        image: "/floors/Rug_005_ambientOcclusion.jpg",
        onSelect: () => {
          const textures = {
            baseColorTexture: roomInstance.textureLoader.load(
              "floors/Rug_005_basecolor.jpg",
            ),
            ambientOcclusionTexture: roomInstance.textureLoader.load(
              "floors/Rug_005_ambientOcclusion.jpg",
            ),
            heightTexture: roomInstance.textureLoader.load(
              "floors/Rug_005_height.png",
            ),
            normalTexture: roomInstance.textureLoader.load(
              "floors/Rug_005_normal.jpg",
            ),
            roughnessTexture: roomInstance.textureLoader.load(
              "floors/Rug_005_roughness.jpg",
            ),
          };

          Object.keys(textures).forEach((key) => {
            textures[key].flipY = false;
            textures[key].encoding = THREE.sRGBColorSpace;
            textures[key].wrapS = THREE.RepeatWrapping;
            textures[key].wrapT = THREE.RepeatWrapping;
            textures[key].repeat.set(28, 23);
          });

          roomInstance.floorMaterial = new THREE.MeshStandardMaterial({
            map: textures.baseColorTexture,
            aoMap: textures.ambientOcclusionTexture,
            normalMap: textures.normalTexture,
            roughnessMap: textures.roughnessTexture,
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 10,
            displacementScale: 0.1,
            displacementBias: -0.05,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (roomInstance.slots_meshes["floor"].includes(child.name)) {
              child.material = roomInstance.floorMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Ungeprüfter Teppichboden als Auslegeware",
        manufacturer: "",
        attributes: {
          sound: 30,
          particulates: 30,
          effluvium: 30,
        },
        explanations: {
          sound:
            "Der Teppichboden als Auslegeware erhöht die Schallabsorption deutlich und reduziert so die Lärmbelastung erheblich.",
          particulates:
            "Der Teppichboden als Auslegeware bindet viele Staubpartikel und erhöht so die Partikelbelastung.",
          effluvium:
            "Der ungeprüfte Teppichboden als Auslegeware kann mehr Schadstoffe abgeben.",
        },
        image: "/floors/Rug_005_ambientOcclusion.jpg",
        onSelect: () => {
          const textures = {
            baseColorTexture: roomInstance.textureLoader.load(
              "floors/Rug_005_basecolor.jpg",
            ),
            ambientOcclusionTexture: roomInstance.textureLoader.load(
              "floors/Rug_005_ambientOcclusion.jpg",
            ),
            heightTexture: roomInstance.textureLoader.load(
              "floors/Rug_005_height.png",
            ),
            normalTexture: roomInstance.textureLoader.load(
              "floors/Rug_005_normal.jpg",
            ),
            roughnessTexture: roomInstance.textureLoader.load(
              "floors/Rug_005_roughness.jpg",
            ),
          };

          Object.keys(textures).forEach((key) => {
            textures[key].flipY = false;
            textures[key].encoding = THREE.sRGBColorSpace;
            textures[key].wrapS = THREE.RepeatWrapping;
            textures[key].wrapT = THREE.RepeatWrapping;
            textures[key].repeat.set(28, 23);
          });

          roomInstance.floorMaterial = new THREE.MeshStandardMaterial({
            map: textures.baseColorTexture,
            aoMap: textures.ambientOcclusionTexture,
            normalMap: textures.normalTexture,
            roughnessMap: textures.roughnessTexture,
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 10,
            displacementScale: 0.1,
            displacementBias: -0.05,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (roomInstance.slots_meshes["floor"].includes(child.name)) {
              child.material = roomInstance.floorMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Vinyl Holzoptik",
        manufacturer: "",
        attributes: {
          sound: -15,
          effluvium: 30,
        },
        explanations: {
          sound:
            "Das Vinyl kann zu Schallreflexionen führen und erhöht so die Lärmbelastung im Raum.",
          effluvium:
            "Das Vinyl kann Schadstoffe abgeben und erhöht so die Ausdünstungen.",
        },
        image: "/floors/pvc-wood.jpg",
        onSelect: () => {
          const textures = {
            baseColorTexture: roomInstance.textureLoader.load(
              "floors/pvc-wood.jpg",
            ),
            ambientOcclusionTexture: roomInstance.textureLoader.load(
              "floors/pvc-wood.jpg",
            ),
          };

          Object.keys(textures).forEach((key) => {
            textures[key].flipY = false;
            textures[key].encoding = THREE.sRGBColorSpace;
            textures[key].wrapS = THREE.RepeatWrapping;
            textures[key].wrapT = THREE.RepeatWrapping;
            textures[key].repeat.set(12, 12);
          });

          roomInstance.floorMaterial = new THREE.MeshStandardMaterial({
            color: 0xdedede,
            map: textures.baseColorTexture,
            aoMap: textures.ambientOcclusionTexture,
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 7,
            displacementScale: 0.1,
            displacementBias: -0.05,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (roomInstance.slots_meshes["floor"].includes(child.name)) {
              child.material = roomInstance.floorMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Fliese und geprüfter Fliesenkleber",
        manufacturer: "",
        attributes: {
          sound: -20,
          effluvium: 5,
          comfort: -5,
        },
        explanations: {
          sound:
            "Die Fliesen können zu Schallreflexionen führen und erhöhen so die Lärmbelastung im Raum.",
          effluvium:
            "Die Fliese und der geprüfte Fliesenkleber geben nur wenige Schadstoffe ab.",
          comfort:
            "Die gefühlte Temperatur einer Fliese ist kälter und daher für ein Kinderzimmer ungeeigneter.",
        },
        image: "/floors/Tiles_035_basecolor.jpg",
        onSelect: () => {
          const textures = {
            baseColorTexture: roomInstance.textureLoader.load(
              "floors/Tiles_035_basecolor.jpg",
            ),
            ambientOcclusionTexture: roomInstance.textureLoader.load(
              "floors/Tiles_035_ambientOcclusion.jpg",
            ),
            heightTexture: roomInstance.textureLoader.load(
              "floors/Tiles_035_height.png",
            ),
            normalTexture: roomInstance.textureLoader.load(
              "floors/Tiles_035_normal.jpg",
            ),
            roughnessTexture: roomInstance.textureLoader.load(
              "floors/Tiles_035_roughness.jpg",
            ),
          };

          Object.keys(textures).forEach((key) => {
            textures[key].flipY = false;
            textures[key].encoding = THREE.sRGBColorSpace;
            textures[key].wrapS = THREE.RepeatWrapping;
            textures[key].wrapT = THREE.RepeatWrapping;
            textures[key].repeat.set(12, 12);
          });

          roomInstance.floorMaterial = new THREE.MeshStandardMaterial({
            color: 0xdedede,
            map: textures.baseColorTexture,
            aoMap: textures.ambientOcclusionTexture,
            normalMap: textures.normalTexture,
            roughnessMap: textures.roughnessTexture,
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 7,
            displacementScale: 0.1,
            displacementBias: -0.05,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (roomInstance.slots_meshes["floor"].includes(child.name)) {
              child.material = roomInstance.floorMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
    ],
    floor_boards: [
      {
        title: "Einfache Fußleisten",
        manufacturer: "",
        attributes: {},
        explanations: {},
        image: undefined,
        onSelect: () => {
          roomInstance.floorBoardMaterial = new THREE.MeshStandardMaterial({
            color: new THREE.Color(0xefefef),
            lightMap: roomInstance.getLightMapMaterial().map,
            lightMapIntensity: 8,
          });

          roomInstance.group.children[0].children.forEach((child) => {
            if (
              roomInstance.slots_meshes["floor_boards"].includes(child.name)
            ) {
              child.material = roomInstance.floorBoardMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
    ],
    walls: [
      {
        title: "Mineralische Silikatfarbe",
        manufacturer: "",
        attributes: { effluvium: 5 },
        explanations: {
          effluvium:
            "Die mineralische Silikatfarbe gibt nur wenige Schadstoffe ab.",
        },
        image: undefined,
        color: "#d6abac",
        onSelect: () => {
          roomInstance.group.children[0].children.forEach((child) => {
            roomInstance.wallMaterial = new THREE.MeshStandardMaterial({
              color: new THREE.Color(0xd6abac),
              lightMap: roomInstance.getLightMapMaterial().map,
              lightMapIntensity: 5,
            });

            if (roomInstance.slots_meshes["walls"].includes(child.name)) {
              child.material = roomInstance.wallMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Geprüfte Dispersionsfarbe",
        manufacturer: "",
        attributes: { effluvium: 5 },
        explanations: {
          effluvium:
            "Die geprüfte Dispersionsfarbe gibt nur wenige Schadstoffe ab.",
        },
        image: undefined,
        color: "#c2cdbc",
        onSelect: () => {
          roomInstance.group.children[0].children.forEach((child) => {
            roomInstance.wallMaterial = new THREE.MeshStandardMaterial({
              color: new THREE.Color(0xc2cdbc),
              lightMap: roomInstance.getLightMapMaterial().map,
              lightMapIntensity: 5,
            });

            if (roomInstance.slots_meshes["walls"].includes(child.name)) {
              child.material = roomInstance.wallMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Ungeprüfte Dispersionsfarbe",
        manufacturer: "",
        attributes: { effluvium: 30 },
        explanations: {
          effluvium:
            "Die ungeprüfte Dispersionsfarbe kann mehr Schadstoffe abgeben.",
        },
        image: undefined,
        color: "#c2cdbc",
        onSelect: () => {
          roomInstance.group.children[0].children.forEach((child) => {
            roomInstance.wallMaterial = new THREE.MeshStandardMaterial({
              color: new THREE.Color(0xc2cdbc),
              lightMap: roomInstance.getLightMapMaterial().map,
              lightMapIntensity: 5,
            });

            if (roomInstance.slots_meshes["walls"].includes(child.name)) {
              child.material = roomInstance.wallMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Geprüfte Acrylatfarbe / Latexfarbe",
        manufacturer: "",
        attributes: {
          effluvium: 5,
          humidity: 5,
        },
        explanations: {
          effluvium:
            "Die geprüfte Acrylatfarbe gibt nur wenige Schadstoffe ab.",
          humidity:
            "Die Acrylat- bzw. Latexfarbe ist weniger diffusionsoffen. Daher erhöht sich die Luftfeuchtigkeit.",
        },
        image: undefined,
        color: "#DEDEDE",
        onSelect: () => {
          roomInstance.group.children[0].children.forEach((child) => {
            roomInstance.wallMaterial = new THREE.MeshStandardMaterial({
              color: new THREE.Color(0xdedede),
              lightMap: roomInstance.getLightMapMaterial().map,
              lightMapIntensity: 5,
            });

            if (roomInstance.slots_meshes["walls"].includes(child.name)) {
              child.material = roomInstance.wallMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
      {
        title: "Ungeprüfte Acrylatfarbe / Latexfarbe",
        manufacturer: "",
        attributes: {
          effluvium: 30,
          humidity: 5,
        },
        explanations: {
          effluvium:
            "Die ungeprüfte Acrylatfarbe kann mehr Schadstoffe abgeben.",
          humidity:
            "Die Acrylat- bzw. Latexfarbe ist weniger diffusionsoffen. Daher erhöht sich die Luftfeuchtigkeit.",
        },
        image: undefined,
        color: "#DEDEDE",
        onSelect: () => {
          roomInstance.group.children[0].children.forEach((child) => {
            roomInstance.wallMaterial = new THREE.MeshStandardMaterial({
              color: new THREE.Color(0xdedede),
              lightMap: roomInstance.getLightMapMaterial().map,
              lightMapIntensity: 5,
            });

            if (roomInstance.slots_meshes["walls"].includes(child.name)) {
              child.material = roomInstance.wallMaterial;
            }
          });
        },
        onUnselect: () => {
          // floors should just change, not be unselected
        },
      },
    ],
    interior_walls: [
      {
        title: "Kalksandstein mit Putz",
        manufacturer: "",
        attributes: {
          humidity: -10,
          sound: 15,
        },
        explanations: {
          humidity:
            "Massives Mauerwerk mit Putz reguliert die Luftfeuchtigkeit natürlich und reduziert sie effektiv.",
          sound:
            "Die massive Bauweise bietet eine sehr gute Schallabsorption und verbessert die Raumakustik erheblich.",
        },
        image: undefined,
        color: "#a9a9a9",
        onSelect: () => {},
        onUnselect: () => {},
      },
      {
        title: "Porenbeton mit Putz",
        manufacturer: "ytong",
        attributes: {
          humidity: -10,
          sound: 10,
          comfort: 5,
        },
        explanations: {
          humidity:
            "Porenbeton mit Putz hat gute feuchtigkeitsregulierende Eigenschaften und reduziert die Luftfeuchtigkeit.",
          sound:
            "Die Porenbeton-Konstruktion bietet gute Schallabsorption und verbessert die Raumakustik.",
          comfort:
            "Der Porenbetonstein sorgt durch seine gute Dämmeigenschaften für weniger Energiekosten.",
        },
        image: undefined,
        color: "#a9a9a9",
        onSelect: () => {},
        onUnselect: () => {},
      },
      {
        title: "Einfache Trockenbauwand",
        manufacturer: "",
        attributes: {
          sound: -15,
        },
        explanations: {
          sound:
            "Einfache Trockenbauwände haben schlechtere Schalldämmeigenschaften und können zu erhöhten Schallübertragungen in Nebenräume führen.",
        },
        image: undefined,
        color: "#a9a9a9",
        onSelect: () => {},
        onUnselect: () => {},
      },
      {
        title: "OSB-Beplankung mit Gipsfaserplatte",
        manufacturer: "",
        attributes: {
          effluvium: 10,
          sound: -10,
        },
        explanations: {
          effluvium:
            "OSB-Platten können Formaldehyd und andere Schadstoffe abgeben und erhöhen so die Ausdünstungen im Raum.",
          sound:
            "Eine OSB-Beplankung mit Gipsfaserplatten hat keine guten Schalldämmeigenschaften und können zu erhöhten Schallübertragungen in Nebenräume führen.",
        },
        image: undefined,
        color: "#a9a9a9",
        onSelect: () => {},
        onUnselect: () => {},
      },
    ],
    sloping_roof: [
      {
        title: "Fermacell",
        manufacturer: "",
        attributes: {},
        explanations: {},
        image: undefined,
        color: "#a28df3",
        onSelect: () => {},
        onUnselect: () => {},
      },
      {
        title: "Ungeprüfte Gipsfaserplatten",
        manufacturer: "",
        attributes: {
          effluvium: 10,
        },
        explanations: {
          effluvium:
            "Die ungeprüften Gipsfaserplatten können Schadstoffe abgeben.",
        },
        image: undefined,
        color: "#a28df3",
        onSelect: () => {},
        onUnselect: () => {},
      },
    ],
    heating: [
      {
        title: "Wärmepumpe ohne Kühlfunktion",
        manufacturer: "nibe",
        attributes: {
          comfort: 10,
        },
        explanations: {
          comfort:
            "Die Wärmepumpe erhöht den Komfort und senkt die Energiekosten (z.B. durch Nutzung von Digitalstrom).",
        },
        image: undefined,
        color: "#FBB741",
        onSelect: () => {},
        onUnselect: () => {
          // Component dependencies are now handled automatically by removeIncompatibleComponents
        },
      },
      {
        title: "Wärmepumpe mit Kühlfunktion",
        manufacturer: "nibe",
        attributes: {
          comfort: 10,
          temperature: -3,
        },
        explanations: {
          comfort:
            "Die Wärmepumpe erhöht den Komfort und senkt die Energiekosten (z.B. durch Nutzung von Digitalstrom).",
          temperature:
            "Die Kühlfunktion sorgt dafür, dass die Raumtemperatur bei steigenden Außentemperaturen angenehm bleibt.",
        },
        image: undefined,
        color: "#FBB741",
        onSelect: () => {},
        onUnselect: () => {
          // Component dependencies are now handled automatically by removeIncompatibleComponents
        },
      },
      {
        title: "Pelletheizung",
        manufacturer: "",
        attributes: {
          comfort: -10,
          particulates: 5,
        },
        explanations: {
          comfort:
            "Der Platzbedarf für die Pelletheizung und Lagerung ist höher als bei einer Wärmepumpe.",
          particulates:
            "Moderne Pelletheizungen stoßen wenig, aber messbaren Feinstaub aus.",
        },
        image: undefined,
        color: "#FBB741",
        onSelect: () => {
          // Component dependencies are now handled automatically by removeIncompatibleComponents
        },
        onUnselect: () => {},
      },
    ],
    ventilation: [
      {
        title: "Reine Fensterlüftung",
        manufacturer: "",
        attributes: {
          temperature: 3,
          humidity: -15,
          particulates: 20,
          effluvium: -5,
          sound: -10,
          comfort: -30,
        },
        explanations: {
          temperature:
            "Die Fensterlüftung kann zu unkontrollierten Temperaturschwankungen führen.",
          humidity:
            "Es findet keine kontrollierte Feuchteabfuhr statt. Das kann zu Kondensat und Schimmel führen.",
          particulates:
            "Durch das Öffnen der Fenster können Pollen, Feinstaub oder Gerüche ungefiltert in den Raum gelangen.",
          effluvium:
            "Durch das geöffnete Fenster findet keine Filterung der Luft statt. So können Pollen, Staub und Schadstoffe in den Raum gelangen.",
          sound:
            "Die Fensterlüftung kann zu Lärmbelastung von außen führen und erhöht so die Schallbelastung.",
          comfort:
            "Die manuelle Fensterlüftung sorgt für erhöhten Aufwand durch regelmäßiges Lüften, sorgt für Energieverlust und erhöht das Einbruchrisiko.",
        },
        image: undefined,
        color: "#77ACC7",
        onSelect: () => {},
        onUnselect: () => {},
      },
      {
        title: "Lüftungsanlage kombiniert und steuerbar mit Wärmepumpe",
        manufacturer: "nibe",
        required_components: { heating: [0, 1] },
        attributes: {
          temperature: -1,
          humidity: -25,
          particulates: -10,
          effluvium: -10,
          comfort: 10,
        },
        explanations: {
          temperature:
            "Durch die Lüftungsfunktion „Night Cooling“ lässt sich die kühle Nachtluft gezielt dazu einsetzen, die Raumtemperatur zu senken.",
          humidity:
            "Die Lüftungsanlage reduziert die Luftfeuchtigkeit sehr effektiv - vor allem durch die Funktion „Night Cooling“.",
          particulates:
            "Die Lüftungsanlage filtert Staubpartikel aus der Luft.",
          effluvium:
            "Die Lüftungsanlage sorgt dafür, dass Feuchtigkeit und Gerüche ständig aus den belasteten Räumen abgeführt und durch frische gefilterte Luft ersetzt werden. Dabei werden Reste von TVOCs aus der Raumluft entfernt.",
          comfort:
            "Die automatische Lüftung sorgt für eine konstant gute Luftqualität und senkt durch Wärmerückgewinnung die Energiekosten. Zusätzlich lassen sich Lüftung und Wärmepumpe zentral über eine App gemeinsam steuern.",
        },
        image: undefined,
        color: "#77ACC7",
        onSelect: () => {},
        onUnselect: () => {},
      },
      {
        title:
          "Lüftungsanlage mit Kreuzgegenstrom-Wärmetauscher steuerbar mit Wärmepumpe",
        manufacturer: "nibe",
        required_components: { heating: [0, 1] },
        attributes: {
          temperature: -1,
          humidity: -25,
          particulates: -10,
          effluvium: -10,
          sound: 5,
          comfort: 10,
        },
        explanations: {
          temperature:
            "Durch die Lüftungsfunktion „Night Cooling“ lässt sich die kühle Nachtluft gezielt dazu einsetzen, die Raumtemperatur zu senken.",
          humidity:
            "Die Lüftungsanlage reduziert die Luftfeuchtigkeit sehr effektiv - vor allem durch die Funktion „Night Cooling“.",
          particulates:
            "Die Lüftungsanlage filtert Staubpartikel aus der Luft.",
          effluvium:
            "Die Lüftungsanlage sorgt dafür, dass Feuchtigkeit und Gerüche ständig aus den belasteten Räumen abgeführt und durch frische gefilterte Luft ersetzt werden. Dabei werden Reste von TVOCs aus der Raumluft entfernt.",
          sound:
            "Die zentrale Lüftungsanlage sorgt dafür, dass weniger Außenschall ins Haus gelangt.",
          comfort:
            "Die automatische Lüftung sorgt für eine konstant gute Luftqualität und senkt durch Wärmerückgewinnung die Energiekosten. Zusätzlich lassen sich Lüftung und Wärmepumpe zentral über eine App gemeinsam steuern.",
        },
        image: undefined,
        color: "#77ACC7",
        onSelect: () => {},
        onUnselect: () => {},
      },
      {
        title:
          "Dezentrale Lüftung mit Wärmerückgewinnung nicht steuerbar mit Wärmepumpe",
        manufacturer: "",
        attributes: {
          humidity: -20,
          particulates: -10,
          effluvium: -10,
          sound: -5,
        },
        explanations: {
          humidity:
            "Die Lüftungsanlage reduziert die Luftfeuchtigkeit sehr effektiv.",
          particulates:
            "Die Lüftungsanlage filtert Staubpartikel aus der Luft.",
          effluvium:
            "Die Lüftungsanlage sorgt dafür, dass Feuchtigkeit und Gerüche ständig aus den belasteten Räumen abgeführt und durch frische gefilterte Luft ersetzt werden. Dabei werden Reste von TVOCs aus der Raumluft entfernt.",
          sound: "Die dezentrale Lüftung kann zu leichten Geräuschen führen.",
        },
        image: undefined,
        color: "#77ACC7",
        onSelect: () => {},
        onUnselect: () => {},
      },
    ],
    smart_home: [
      {
        title: "Smart-Home: WLAN",
        manufacturer: "",
        attributes: {
          temperature: -0.5,
          comfort: 10,
        },
        explanations: {
          temperature:
            "Durch die intelligente Steuerung können die Rollläden automatisch nach der Sonneneinstrahlung runterfahren und lassen das Haus nicht erhitzen.",
          comfort:
            "Verschiedene Systeme und Komponenten im eigenen Zuhause werden intelligent vernetzt und automatisiert. Dies sorgt für mehr Komfort, Sicherheit und Energieeffizienz.",
        },
        image: undefined,
        color: "#a28df3",
        onSelect: () => {},
        onUnselect: () => {
          delete roomInstance.slots_components["smart_home"];
        },
      },
      {
        title: "Smart-Home: Kabelgebunden/BUS-System",
        manufacturer: "busch_jaeger",
        attributes: {
          temperature: -0.5,
          comfort: 20,
        },
        explanations: {
          temperature:
            "Durch die intelligente Steuerung können die Rollläden automatisch nach der Sonneneinstrahlung runterfahren und lassen das Haus nicht erhitzen.",
          comfort:
            "Verschiedene Systeme und Komponenten im eigenen Zuhause werden intelligent vernetzt und automatisiert. Dies sorgt für mehr Komfort, Sicherheit und Energieeffizienz. Das kabelgebundene System sorgt zusätzlich für wenig Funkstrahlung (Elektrosmog) und als geschlossenes System ist es von außerhalb nicht angreifbar.",
        },
        image: undefined,
        color: "#a28df3",
        onSelect: () => {},
        onUnselect: () => {
          delete roomInstance.slots_components["smart_home"];
        },
      },
    ],
    app: [
      {
        title: "Mittelstädt Bauherren-App",
        manufacturer: "mittelstaedt",
        attributes: {
          comfort: 10,
        },
        explanations: {
          comfort:
            "Mit der Bauherren-Service-App haben Sie Sie alle aktuellen Unterlagen im „Digitalen Bauordner” immer griffbereit, werden über Checklisten an wichtige Aufgaben und Wartungen erinnert und können Ansprechpartner bei Mittelstädt sowie die beteiligten Gewerke im Adressbuch einsehen.",
        },
        image: undefined,
        color: "#f87171",
        onSelect: () => {},
        onUnselect: () => {
          delete roomInstance.slots_components["app"];
        },
      },
    ],
    vacuum: [
      {
        title: "Zentralstaubsaugeranlage",
        manufacturer: "",
        attributes: {
          particulates: -15,
        },
        explanations: {
          particulates:
            "Die Zentralstaubsaugeranlage reduziert die Staubpartikelbelastung im Raum erheblich, da sie den Staub direkt nach draußen befördert.",
        },
        image: undefined,
        color: "#a9a9a9",
        onSelect: () => {},
        onUnselect: () => {},
      },
    ],
    solar: [
      {
        title: "PV-Anlage",
        manufacturer: "",
        attributes: {
          comfort: 10,
        },
        explanations: {
          comfort:
            "Die PV-Anlage bietet eine umweltfreundliche Energieversorgung und Kosteneinsparungen.",
        },
        image: undefined,
        color: "#fcd34d",
        onSelect: () => {},
        onUnselect: () => {
          delete roomInstance.slots_components["solar"];
        },
      },
      {
        title: "PV-Anlage steuerbar mit Wärmepumpe",
        manufacturer: "",
        required_components: { heating: [0, 1] },
        attributes: {
          comfort: 20,
        },
        explanations: {
          comfort:
            "Die intelligente Verknüpfung von PV-Anlage und Wärmepumpe optimiert den Eigenverbrauch und maximiert die Effizienz des Systems.",
        },
        image: undefined,
        color: "#fcd34d",
        onSelect: () => {},
        onUnselect: () => {
          delete roomInstance.slots_components["solar"];
        },
      },
    ],
  };
}
